// src/app/modules/AccessControl/pages/AccessControl.jsx
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import * as actions from "../../Dashbord/_redux/dashboard/Actions";
import { useOrgConfig } from "../../../../hooks/useOrgConfig";
import './access-control.scss';

import { ValidationModal }      from "./components/ValidationModal";
import { GenerateCodeModal }    from "./components/GenerateCodeModal";
import { CheckoutConfirmModal } from "./components/CheckoutConfirmModal";
import { StaffCheckInModal }    from "./components/StaffCheckInModal";
import { ResidentAccessModal }  from "./components/ResidentAccessModal";
import { QuickActionsModal }    from "./components/QuickActionsModal";
import { AccessLogsTable }      from "./AccessLogsTable";
import { ActivePeopleTable }    from "./ActivePeopleTable";
import { useFeatureGate }       from '../../Dashbord/components/FeatureGate';

import {
  Key, Clock, CheckCircle, XCircle, Plus, Search,
  Shield, Users, UserCheck, ShieldCheck, Home,
  Activity, Filter, Zap,
} from 'lucide-react';

export function AccessControl() {
  const dispatch = useDispatch();
  const { getLabel, hasFeature, showModule, org } = useOrgConfig();
  const { guardAction } = useFeatureGate();

  // ── State ────────────────────────────────────────────────────────────────
  const [accessCode,      setAccessCode]      = useState('');
  const [currentPage,     setCurrentPage]     = useState(1);
  const [perPage]                             = useState(10);

  const [showValidation,     setShowValidation]     = useState(false);
  const [showGenerate,       setShowGenerate]        = useState(false);
  const [showCheckout,       setShowCheckout]        = useState(false);
  const [showStaffCheckIn,   setShowStaffCheckIn]    = useState(false);
  const [showResidentAccess, setShowResidentAccess]  = useState(false);
  const [showQuickActions,   setShowQuickActions]    = useState(false);

  const [selectedLog,      setSelectedLog]      = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  const [validating,   setValidating]   = useState(false);
  const [checkingIn,   setCheckingIn]   = useState(false);
  const [checkingOut,  setCheckingOut]  = useState(false);

  const [statusFilter, setStatusFilter] = useState('checked_in');
  const [typeFilter,   setTypeFilter]   = useState('');
  const [searchTerm,   setSearchTerm]   = useState('');
  const [viewMode,     setViewMode]     = useState('logs');

  const {
    access_code_stats, accessLogs, accessLogsMeta,
    capacityStatus, unifiedDashboard, loading,
  } = useSelector((state) => ({
    access_code_stats: state.dashboard.accessCodeStats,
    accessLogs:        state.dashboard.accessLogs,
    accessLogsMeta:    state.dashboard.accessLogsMeta,
    capacityStatus:    state.dashboard.capacityStatus,
    unifiedDashboard:  state.dashboard.unifiedDashboard,
    loading:           state.dashboard.actionsLoading,
  }), shallowEqual);

  useEffect(() => {
    if (org?.organization_id) loadData();
  }, [org?.organization_id]);

  function loadData() {
    if (!org?.organization_id) return;
    dispatch(actions.fetchAllData('access_code_stats', org.organization_id));
    dispatch(actions.fetchAllData('capacity_status',   org.organization_id));
    dispatch(actions.fetchAllData('unified_dashboard', org.organization_id));
    loadAccessLogs();
  }

  function loadAccessLogs() {
    if (!org?.organization_id) return;
    const filters = { page: currentPage, perPage };
    if (statusFilter) filters.status   = statusFilter;
    if (typeFilter)   filters.log_type = typeFilter;
    if (searchTerm)   filters.search   = searchTerm.trim();
    dispatch(actions.fetchAllData('access_logs', org.organization_id, filters));
  }

  const formatCode = (input) => {
    const cleaned = input.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    const chunks  = cleaned.match(/.{1,3}/g);
    return chunks ? chunks.join('-') : '';
  };

  async function validateCode() {
    if (!accessCode || !org?.organization_id) return;
    setValidating(true);
    try {
      const result = await dispatch(actions.addData('validate_code', {
        code: accessCode.replace(/[^a-zA-Z0-9]/g, ''),
        organization_id: org.organization_id,
      }, org.organization_id));
      setValidationResult(result?.valid ? result : { valid: false, error: 'Invalid or expired access code' });
      if (result?.valid) {
        loadAccessLogs();
      }
    } catch (e) {
      setValidationResult({ valid: false, error: e.response?.data?.message || 'Invalid or expired access code' });
    } finally {
      setValidating(false);
      setShowValidation(true);
      setAccessCode('');
    }
  }

  function handleCheckIn() {
    setCheckingIn(true);
    setTimeout(() => { setCheckingIn(false); setShowValidation(false); loadData(); }, 1000);
  }

  function handleCheckout(logId, accessLog) {
    setSelectedLog(accessLog);
    setShowCheckout(true);
  }

  async function confirmCheckout() {
    if (!selectedLog) return;
    setCheckingOut(true);
    try {
      await dispatch(actions.addData('checkout_visitor', { log_id: selectedLog.log_id }, org.organization_id));
      setShowCheckout(false);
      setSelectedLog(null);
      loadData();
    } catch (e) { console.error(e); }
    finally { setCheckingOut(false); }
  }

  // ── Derived values ────────────────────────────────────────────────────────
  const todayTotal =
    (unifiedDashboard?.statistics?.visitors?.today_total  || 0) +
    (unifiedDashboard?.statistics?.staff?.today_total     || 0) +
    (unifiedDashboard?.statistics?.residents?.today_total || 0);

  const liveStats = [
    {
      icon: <UserCheck size={16} />, bg: '#F0FDF4', color: '#16a34a',
      label: getLabel('activeVisitors'),
      value: unifiedDashboard?.statistics?.visitors?.currently_active || 0,
    },
    ...(hasFeature('hasStaff') ? [{
      icon: <ShieldCheck size={16} />, bg: '#EFF6FF', color: '#2563EB',
      label: `${getLabel('staff')} on Duty`,
      value: unifiedDashboard?.statistics?.staff?.currently_active || 0,
    }] : []),
    ...(hasFeature('hasResidents') ? [{
      icon: <Home size={16} />, bg: '#F0EEFF', color: '#5341C4',
      label: `${getLabel('residents')} Inside`,
      value: unifiedDashboard?.statistics?.residents?.currently_inside || 0,
    }] : []),
    {
      icon: <Activity size={16} />, bg: '#FFFBEB', color: '#D97706',
      label: getLabel('todayEntries'),
      value: todayTotal,
    },
  ];

  const codeStats = [
    { icon: <CheckCircle size={15} />, bg: '#F0FDF4', color: '#16a34a', value: access_code_stats?.active  || 0, label: 'Active' },
    { icon: <Key         size={15} />, bg: '#EFF6FF', color: '#2563EB', value: access_code_stats?.used    || 0, label: 'Used Today' },
    { icon: <Clock       size={15} />, bg: '#FFFBEB', color: '#D97706', value: access_code_stats?.expired || 0, label: 'Expired' },
    { icon: <XCircle     size={15} />, bg: '#FEF2F2', color: '#DC2626', value: access_code_stats?.revoked || 0, label: 'Revoked' },
  ];

  return (
    <>
      <div className="access-control-page">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="ac-header">
          <div className="ac-header__left">
            <h1>{getLabel('accessControl')}</h1>
            <p>{org?.name} · Real-time access control</p>
          </div>
          <div className="ac-header__actions">
            <button className="ac-btn ac-btn--secondary" onClick={() => setShowQuickActions(true)}>
              <Zap size={13} />Quick Actions
            </button>
            <button className="ac-btn ac-btn--primary" onClick={() => guardAction(() => setShowGenerate(true))}>
              <Plus size={13} />Generate Code
            </button>
          </div>
        </div>

        {/* ── Validate + live stats strip ──────────────────────────────────── */}
        <div className="ac-strip">

          {/* Left: code input */}
          <div className="ac-validate">
            <div className="ac-validate__head">
              <div className="ac-validate__icon"><Shield size={16} /></div>
              <div>
                <div className="ac-validate__title">Validate Access Code</div>
                <div className="ac-validate__sub">Scan or enter visitor access code</div>
              </div>
            </div>
            <div className="ac-validate__form">
              <input
                className="ac-validate__input"
                type="text"
                placeholder="e.g. ABC-123"
                value={accessCode}
                onChange={(e) => setAccessCode(formatCode(e.target.value))}
                onKeyPress={(e) => e.key === 'Enter' && validateCode()}
              />
              <button
                className="ac-validate__btn"
                onClick={validateCode}
                disabled={validating || !accessCode}
              >
                {validating ? 'Checking…' : 'Validate'}
              </button>
            </div>
          </div>

          {/* Right: live stat chips */}
          <div className="ac-stats">
            {liveStats.map((s, i) => (
              <div key={i} className="ac-stat">
                <div className="ac-stat__icon" style={{ background: s.bg, color: s.color }}>
                  {s.icon}
                </div>
                <div>
                  <div className="ac-stat__label">{s.label}</div>
                  <div className="ac-stat__value">{s.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Code stats — only when no staff feature ───────────────────── */}
        {!hasFeature('hasStaff') && (
          <div className="ac-card">
            <div className="ac-card__header">
              <div className="ac-card__title"><Key size={14} />Access Code Statistics</div>
            </div>
            <div className="ac-card__body" style={{ padding: 0 }}>
              <div className="ac-code-stats">
                {codeStats.map((s, i) => (
                  <div key={i} className="ac-code-stat">
                    <div className="ac-code-stat__icon" style={{ background: s.bg, color: s.color }}>
                      {s.icon}
                    </div>
                    <div>
                      <div className="ac-code-stat__value">{s.value}</div>
                      <div className="ac-code-stat__label">{s.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Filters bar ──────────────────────────────────────────────────── */}
        <div className="ac-card">
          <div className="ac-card__body" style={{ padding: '11px 16px' }}>
            <div className="ac-filters">

              <div className="ac-view-toggle">
                <button className={viewMode === 'logs'   ? 'active' : ''} onClick={() => setViewMode('logs')}>
                  <Activity size={13} />All Logs
                </button>
                <button className={viewMode === 'active' ? 'active' : ''} onClick={() => setViewMode('active')}>
                  <Users size={13} />{getLabel('residents')} Inside
                </button>
              </div>

              <div className="ac-search">
                <Search size={14} />
                <input
                  placeholder="Search by name, code, phone…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && loadAccessLogs()}
                />
              </div>

              {(hasFeature('hasStaff') || hasFeature('hasResidents')) && (
                <select
                  className="ac-select"
                  value={typeFilter}
                  onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">All Types</option>
                  <option value="visitor_entry">Visitors</option>
                  {hasFeature('hasStaff')     && <option value="staff_shift">Staff</option>}
                  {hasFeature('hasResidents') && <option value="resident_access">Residents</option>}
                </select>
              )}

              <select
                className="ac-select"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="">All Status</option>
                <option value="checked_in">Checked In</option>
                <option value="checked_out">Checked Out</option>
                {/* <option value="denied">Denied</option> */}
                {hasFeature('hasStaff') && <option value="on_break">On Break</option>}
              </select>

              <button className="ac-apply-btn" onClick={() => { setCurrentPage(1); loadAccessLogs(); }}>
                <Filter size={13} />Apply
              </button>

            </div>
          </div>
        </div>

        {/* ── Data table ───────────────────────────────────────────────────── */}
        <div className="ac-card">
          <div className="ac-card__header">
            <div className="ac-card__title">
              {viewMode === 'active' ? getLabel('currentlyInside') : 'Access Logs'}
            </div>
            <span className="ac-card__meta">
              {viewMode === 'active'
                ? `${capacityStatus?.total || 0} people currently inside`
                : `${accessLogsMeta?.totalResults || 0} total entries`}
            </span>
          </div>
          <div className="ac-table-wrap">
            {viewMode === 'logs' ? (
              <AccessLogsTable
                onCheckout={handleCheckout}
                onPageChange={(p) => { setCurrentPage(p); loadAccessLogs(); }}
                currentPage={currentPage}
                perPage={perPage}
                getLabel={getLabel}
                hasFeature={hasFeature}
              />
            ) : (
              <ActivePeopleTable
                onCheckout={handleCheckout}
                getLabel={getLabel}
                hasFeature={hasFeature}
              />
            )}
          </div>
        </div>

      </div>

      {/* ── Modals ───────────────────────────────────────────────────────────── */}
      <ValidationModal
        show={showValidation}   onHide={() => setShowValidation(false)}
        validationResult={validationResult}
        onCheckIn={handleCheckIn}
        loading={checkingIn}    getLabel={getLabel}
      />
      <GenerateCodeModal
        show={showGenerate}     onHide={() => setShowGenerate(false)}
        organizationId={org?.organization_id}
        onSuccess={loadData}    getLabel={getLabel}
      />
      <CheckoutConfirmModal
        show={showCheckout}     onHide={() => { setShowCheckout(false); setSelectedLog(null); }}
        accessLog={selectedLog} onConfirm={confirmCheckout}
        loading={checkingOut}   getLabel={getLabel}
      />
      {hasFeature('hasStaff') && (
        <StaffCheckInModal
          show={showStaffCheckIn}  onHide={() => setShowStaffCheckIn(false)}
          organizationId={org?.organization_id}
          onSuccess={loadData}     getLabel={getLabel}
        />
      )}
      {hasFeature('hasResidents') && (
        <ResidentAccessModal
          show={showResidentAccess} onHide={() => setShowResidentAccess(false)}
          organizationId={org?.organization_id}
          onSuccess={loadData}      getLabel={getLabel}
        />
      )}
      <QuickActionsModal
        show={showQuickActions}   onHide={() => setShowQuickActions(false)}
        onGenerateCode={()    => { setShowQuickActions(false); setShowGenerate(true); }}
        onStaffCheckIn={()    => { setShowQuickActions(false); setShowStaffCheckIn(true); }}
        onResidentAccess={()  => { setShowQuickActions(false); setShowResidentAccess(true); }}
        hasFeature={hasFeature}   getLabel={getLabel}
      />
    </>
  );
}